<template>
  <div ref="searchResults" class="search-results container">
    <BaseTitle tag="h2" class="centered mt-1-5">
      {{ $t("search.results-for", [searchQuery]) }}
    </BaseTitle>

    <!-- If we have brochure results -->
    <InfiniteLoading
      v-if="data?.searchResults.__typename === 'SearchBrochureList'"
      v-slot="{ allData: brochures }"
      :data="data?.searchResults.brochures"
      :has-more-to-load="hasMoreToLoad"
      :pixel-offset="100"
      class="search-results-brochures-grid mb-4"
      @load-more="loadMore"
    >
      <BrochureItem
        v-for="brochure in brochures!"
        :key="brochure.id"
        :brochure="brochure"
        :impression-source="BrochureImpressionSource.Search"
      />
    </InfiniteLoading>

    <!-- If we have offer results -->
    <InfiniteLoading
      v-else-if="data?.searchResults.__typename === 'SearchOfferList'"
      v-slot="{ allData: offers }"
      :data="data?.searchResults.offers"
      :has-more-to-load="hasMoreToLoad"
      :pixel-offset="100"
      class="search-results-offers-grid mb-4"
      @load-more="loadMore"
    >
      <OfferItem
        v-for="offer in offers!"
        :key="offer.id"
        :offer="offer"
        :hotspot-id="offer.hotspot.id"
      />
    </InfiniteLoading>

    <!-- If we have shop results -->
    <InfiniteLoading
      v-else-if="data?.searchResults.__typename === 'SearchShopList'"
      v-slot="{ allData: shops }"
      :data="data?.searchResults.shops"
      :has-more-to-load="hasMoreToLoad"
      :pixel-offset="100"
      class="search-results-offers-grid mb-4"
      @load-more="loadMore"
    >
      <ShopItem v-for="shop in shops" :key="shop.id" :shop="shop" />
    </InfiniteLoading>

    <!-- No results -->
    <BaseText
      v-else-if="data?.searchResults.__typename === 'SearchNoResults'"
      class="centered-block no-results-text"
    >
      {{ $t("search.no-results") }}
    </BaseText>

    <!-- Loading state -->
    <BaseLoading v-if="fetching" class="mt-4 mb-4" />
  </div>
  <DownloadAppBanner :hide-mobile="true" />
</template>

<script setup lang="ts">
import {
  useSearchResultQuery,
  type SearchResultsInput,
  BrochureImpressionSource,
  SearchMethod,
} from "~/graphql/generated";

const props = defineProps<{
  searchInput: SearchResultsInput;
  searchQuery: string; // Only for display purposes
}>();

const pagination = reactive({
  offset: 0,
  limit: 20,
});

// The method used (gets returned on the first result), is needed to keep track in pagination
const searchMethod = ref<SearchMethod | undefined>(undefined);

// Get the users location (if set)
const location = useLocationState();

const variables = computed(() => {
  return {
    searchResults: {
      ...props.searchInput,
      searchMethod: searchMethod.value,
      ...(location.value && {
        point: {
          latitude: location.value.lat,
          longitude: location.value.long,
        },
      }),
    },
    pagination,
  };
});

const { data, fetching } = await useSearchResultQuery({
  variables,
});

/*
 * Set up infinite loading
 */
const searchResults = ref<HTMLElement | null>(null);
const loadMore = () => {
  if (fetching.value) return;
  pagination.offset += pagination.limit;
  if (data.value?.searchResults?.__typename !== "SearchNoResults") {
    searchMethod.value = data.value?.searchResults?.searchMethod;
  }
};

// Check if there is more data to load by comparing the returned data length to the pagination limit
const hasMoreToLoad = computed(() => {
  if (data.value?.searchResults?.__typename === "SearchBrochureList") {
    return pagination.limit === data.value?.searchResults.brochures.length;
  } else if (data.value?.searchResults?.__typename === "SearchOfferList") {
    return pagination.limit === data.value?.searchResults.offers.length;
  } else if (data.value?.searchResults?.__typename === "SearchShopList") {
    return pagination.limit === data.value?.searchResults.shops.length;
  }
  return false;
});
</script>

<style lang="scss" scoped>
.search-results {
  flex: 1;
}

.no-results-text {
  margin-top: 3rem;
  margin-bottom: 4rem;

  @include for-medium-screen-up {
    margin-bottom: 10rem;
  }
}

.search-results-brochures-grid {
  display: grid;
  gap: 1.5rem;
  justify-content: center;
  grid-template-columns: repeat(auto-fill, minmax(225px, 1fr));

  @include for-extra-small-screen {
    grid-template-columns: 1fr 1fr;
  }

  .no-results-text {
    grid-column: 1 / -1;
  }
}

.search-results-offers-grid {
  display: grid;
  gap: 1.5rem;
  justify-content: center;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));

  @include for-small-screen {
    grid-template-columns: repeat(auto-fill, minmax(225px, 1fr));
  }

  @include for-extra-small-screen {
    grid-template-columns: 1fr 1fr;
  }

  .no-results-text {
    grid-column: 1 / -1;
  }
}
</style>
